/**
 * Support workspace vocabularies (ms-agents v1.9).
 *
 * These are the server's enums, mirrored once here so the queue, the admin
 * dashboard, the filters and the ticket header all speak the same words. Values
 * are sent to the API verbatim — only the labels are ours.
 */

/**
 * Saved views — the `?view=` rail. These replace filtering in the browser: the
 * server applies the same predicates that `GET /support/views/counts` counts,
 * so a chip's number always matches what opening it shows.
 *
 * Snoozed tickets are excluded from every view except `snoozed` (or when an
 * explicit `status` is passed alongside).
 *
 * @type {Array<{ key: string, label: string, description: string }>}
 */
export const VIEWS = [
  { key: "red_zone", label: "Red zone", description: "Breached, still open" },
  { key: "pool", label: "Pool", description: "Unassigned and open" },
  { key: "mine", label: "Mine", description: "Assigned to you" },
  { key: "all_open", label: "All open", description: "Open" },
  { key: "escalated", label: "Escalated", description: "Escalated to you" },
  { key: "waiting", label: "Waiting", description: "Waiting on the customer" },
  { key: "snoozed", label: "Snoozed", description: "Currently snoozed" },
  { key: "resolved_today", label: "Resolved today", description: "Resolved since midnight" },
];

/**
 * Views the server has no `?view=` for, expressed as plain query params.
 *
 * `resolved_today` is the only server view covering closed work, and it stops
 * at midnight — so yesterday's resolved ticket had no route at all. `status=`
 * is a documented filter (§13.2), and passing it explicitly also lifts the
 * queue's default exclusion of snoozed rows.
 *
 * Lowercase because that is the form the pre-v1.9 queue used against this same
 * param and it worked; `ticket_status` uses the uppercase spellings.
 *
 * @type {Object<string, Object>}
 */
export const PSEUDO_VIEWS = {
  resolved_all: { status: "resolved" },
};

/**
 * The extra rail entries backed by {@link PSEUDO_VIEWS}. They carry no count —
 * `views/counts` only knows the server's own eight.
 * @type {Array<{ key: string, label: string, description: string }>}
 */
export const EXTRA_VIEWS = [
  {
    key: "resolved_all",
    label: "Resolved · all",
    description: "Every resolved ticket, not just today's",
  },
];

/**
 * Ticket priority, most urgent first. Drives the row's left bar, the sort
 * order, and which per-priority SLA policy a ticket is measured against.
 *
 * @type {Array<{ key: string, label: string, tone: string }>}
 */
export const PRIORITIES = [
  { key: "urgent", label: "Urgent", tone: "danger" },
  { key: "high", label: "High", tone: "brand" },
  { key: "normal", label: "Normal", tone: "neutral" },
  { key: "low", label: "Low", tone: "info" },
];

/** Priority → label. @type {Object<string, string>} */
export const PRIORITY_LABELS = Object.fromEntries(
  PRIORITIES.map((p) => [p.key, p.label])
);

/**
 * Left-bar colour per priority. Full class strings — Tailwind cannot see
 * interpolated names.
 * @type {Object<string, string>}
 */
export const PRIORITY_BAR_CLASSES = {
  urgent: "bg-danger",
  high: "bg-orange-primary",
  normal: "bg-gray-300",
  low: "bg-info",
};

/** Departments the backend routes and reports on. @type {string[]} */
/**
 * Routing pools. Unlike `category`, this IS a validated backend enum — an
 * unknown value is a 422 — so this list must track the API exactly.
 *
 * `Plan` and `Coach` are v1.9.1 additions. Before them, plan and coaching
 * tickets both fell through to `Other`.
 */
export const DEPARTMENTS = [
  "Billing",
  "Account",
  "App",
  "Plan",
  "Coach",
  "Other",
];

/**
 * Which side the ball is in. `agent` means the next-response clock is running;
 * `customer` pauses it.
 * @type {Array<{ key: string, label: string }>}
 */
export const WAITING_ON = [
  { key: "agent", label: "Waiting on us" },
  { key: "customer", label: "Waiting on customer" },
];

/**
 * Queue sort orders. The default (`oldest`) is unassigned-first then FIFO;
 * `sla` is soonest-deadline first.
 * @type {Array<{ key: string, label: string }>}
 */
export const SORTS = [
  { key: "oldest", label: "Oldest first" },
  { key: "newest", label: "Newest first" },
  { key: "priority", label: "Priority" },
  { key: "sla", label: "SLA deadline" },
];

/** The backend rejects a bulk call carrying more than this many ids. */
export const BULK_MAX_IDS = 100;

/**
 * Activity-trail event types → human labels. Anything unmapped falls back to
 * the raw type, so a newly added backend event still renders.
 * @type {Object<string, string>}
 */
export const ACTIVITY_EVENT_LABELS = {
  CREATED: "Ticket created",
  ROUTED: "Auto-routed",
  ASSIGNED: "Assigned",
  UNASSIGNED: "Returned to pool",
  FIRST_RESPONSE: "First response sent",
  PRIORITY_CHANGED: "Priority changed",
  DEPARTMENT_CHANGED: "Department changed",
  TYPE_CHANGED: "Type changed",
  SUBJECT_CHANGED: "Subject changed",
  TAGGED: "Tagged",
  UNTAGGED: "Untagged",
  SNOOZED: "Snoozed",
  UNSNOOZED: "Woke from snooze",
  MERGED: "Merged",
  ESCALATED: "Escalated",
  SLA_BREACHED: "SLA breached",
  RESOLVED: "Resolved",
  REOPENED: "Reopened",
  CSAT_RECEIVED: "CSAT received",
};

/** Declared agent availability (not socket connectivity). @type {Array<{ key: string, label: string }>} */
export const AGENT_PRESENCE = [
  { key: "available", label: "Available" },
  { key: "away", label: "Away" },
  { key: "offline", label: "Offline" },
];

/**
 * Reopen window after a resolve, in hours — the client may reopen within it.
 * Matches the backend's 24h rule.
 */
export const REOPEN_WINDOW_HOURS = 24;
